import { createExecutionBridge } from './execution-bridge'
import { executePoint } from './executor'
// The tool connects only to accessible documents or an explicitly installed game receiver.
// ds.html installs ll.js before the engine starts; reuse that engine-side hook.
const later = window.setTimeout.bind(window), clear = window.clearTimeout.bind(window)

export function inspectGameFrame(frame, host = window) {
  const base = { speedReady: false, clickReady: false, gameplayReady: false, rate: 1, message: '等待游戏页面加载' }
  if (!frame || frame.isConnected === false) return { ...base, kind: 'loading' }
  let visited = 0, denied = false, root = null, engine = null, hookTarget = null
  function visit(win, depth) {
    if (!win || depth > 4 || ++visited > 24) return
    try {
      const doc = win.document
      if (!doc || !doc.body || win.location.href === 'about:blank' || doc.readyState === 'loading') return
      if (!root) root = { win, doc }
      const hook = win.$hookTimer
      if (hook && typeof hook.setSpeed === 'function' && Number.isFinite(Number(hook.rate))) hookTarget = hookTarget || { win, doc, hook }
      if (win.Laya && win.Laya.__classmap && win.Laya.__classmap['com.game.model.UserObj']) {
        engine = engine || { win, doc, hook }
      }
      for (const nested of doc.querySelectorAll('iframe')) visit(nested.contentWindow, depth + 1)
    } catch (_) { denied = true }
  }
  visit(frame.contentWindow, 0)
  const target = engine || hookTarget
  const speedReady = !!(target && target.hook && typeof target.hook.setSpeed === 'function' && Number.isFinite(Number(target.hook.rate)))
  const nativeClick = !!(host.androidJs && typeof host.androidJs.autoClick === 'function')
  if (!root) return { ...base, kind: denied ? 'cross-origin' : 'loading', clickReady: nativeClick,
    message: denied ? '游戏跨域：需使用同源登录入口；原生连点可在游戏包内使用' : base.message }
  return { ...base, kind: 'connected', speedReady, clickReady: true, gameplayReady: !!engine,
    rate: speedReady ? Number(target.hook.rate) : 1,
    message: engine ? speedReady ? '已连接游戏，加速与玩法已就绪' : '游戏已连接，等待加速组件' : speedReady ? '加速已连接，等待角色进入游戏' : '页面已连接，等待游戏加载',
    target, root }
}

export function createGameConnection({ getFrame, onChange = () => {}, host = window, schedule = later, cancel = clear }) {
  let stopped = false, timer = null, frame = null, lastDoc = null, generation = 0
  let bridge = null
  const notify = info => {
    // Never expose document handles, game URL, tokens or account identifiers in UI state.
    const { kind, speedReady, clickReady, gameplayReady, rate, message } = info
    onChange({ kind, speedReady, clickReady, gameplayReady, rate, message, generation })
    return info
  }
  function scan() {
    let info = inspectGameFrame(getFrame(), host)
    const remote = bridge && bridge.status()
    if (remote) info = { ...info, ...remote, kind: 'receiver', message: '已连接游戏内执行模块', gameplayReady: info.gameplayReady }
    const doc = remote ? remote.document : info.root && info.root.doc
    if (doc !== lastDoc) { lastDoc = doc; generation++ }
    return notify(info)
  }
  bridge = createExecutionBridge({ getFrame, host, changed: () => { if (!stopped) scan() } })
  function reloaded() { generation++; lastDoc = null; bridge.disconnect(); scan() }
  function tick() {
    if (stopped) return
    if (getFrame() !== frame) {
      if (frame) frame.removeEventListener('load', reloaded)
      frame = getFrame()
      if (frame) frame.addEventListener('load', reloaded)
    }
    scan(); timer = schedule(tick, host.document && host.document.hidden ? 2000 : 600)
  }
  return {
    start() { stopped = false; if (timer !== null) cancel(timer); tick() },
    refresh: scan,
    async pickPoint() {
      const result = await bridge.request('pick', null)
      if (!result || result.cancelled) return null
      if (![result.x,result.y].every(n => typeof n === 'number' && Number.isFinite(n) && n >= 0 && n <= 1)) throw new Error('游戏点位未确认')
      return { x: result.x, y: result.y, delay: 500, space: 'game-document' }
    },
    clickPoint(point) {
      if (stopped) throw new Error('游戏连接已关闭')
      if (bridge.status()) {
        if (point.space !== 'game-document') throw new Error('请使用游戏内选点重新设置位置')
        return bridge.request('click', { x: point.x, y: point.y })
      }
      if (point.space === 'game-document') throw new Error('游戏内执行模块已断开，请重新连接')
      return executePoint(getFrame(), point)
    },
    setRate(value) {
      if (stopped) throw new Error('游戏连接已关闭')
      const n = Number(value)
      if (!Number.isFinite(n) || n < .1 || n > 999) throw new Error('倍率需为 0.1 至 999')
      const info = scan()
      if (!info.speedReady) throw new Error(info.kind === 'cross-origin' ? info.message : '游戏加速组件尚未就绪，请等待进入角色')
      if (info.kind === 'receiver') return bridge.request('speed', n).then(result => {
        if (!result || result.rate !== n) throw new Error('游戏未确认倍率')
        return n
      })
      const { win, doc, hook } = info.target
      if (win.document !== doc) throw new Error('游戏已重载，请等待重新连接')
      hook.setSpeed(n)
      if (Number(hook.rate) !== n || win.document !== doc) throw new Error('游戏未确认倍率，请重新连接')
      scan(); return n
    },
    destroy() { stopped = true; bridge.destroy(); cancel(timer); timer = null; if (frame) frame.removeEventListener('load', reloaded); frame = null; lastDoc = null },
  }
}
